import React from "react";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import { toast } from "react-toastify";
import ReplayIcon from "@mui/icons-material/Replay";
import { styled } from "@mui/material/styles";

import DisplayTemplate from "../displayTemplate";
import FIBText from "../renderer/FIBText";
import Button from "../Button";
import HelpText from "../quiz/HelpText";
import { getSpeechConfig, textToSpeech } from "../../store/quiz/quizBaseApi";
import { useStudy } from "../../pages/study/hooks";
import { useQuiz } from "../../pages/quiz/hooks";
import { makeStyles } from "tss-react/mui";
import { Box, Typography } from "@mui/material";
import correctSvg from "../../assets/svgs/speechEvaluationIcons/correct.svg";
import wrongSvg from "../../assets/svgs/speechEvaluationIcons/wrong.svg";

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} arrow />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#fff",
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[4],
    fontSize: 14,
    padding: theme.spacing(1),
    borderRadius: "20px",
    border: "2px solid #69CFFF",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "#69CFFF",
  },
}));

const useStyles = makeStyles()((theme) => ({
  highlight: {
    marginRight: theme.spacing(1),
    color: theme.palette.common.black,
    background: "rgba(255, 199, 74, 0.2)",
    border: "1px solid #FFC74A",
    borderRadius: "10px",
    padding: "8px",
    lineHeight: "2.6",
  },
}));

export default function AsyncTextToSpeech({
  id,
  contentArray,
  style,
  enableTextToAudio,
  autoPlay,
  onAudioPlayback,
  onAudioReplay,
  onAudioPlaybackComplete,
  importantWords,
  readingComplete,
  contentType,
  selected,
  disabled,
  handleAnswerSelection,
  renderDefault = true,
  language,
  contentDisplayType,
  contentUrl,
  mediaStyle,
  overrideHTML,
  helpText,
  syncWithSpeechEvaluation = false,
  templateType,
  speechEvaluated,
  dictionary = [],
  assessmentScreen,
  setAssessmentScreen,
  scoreEvaluated,
  ...props
}) {
  const Component = () => {
    const {
      scores,
      setScores,
      failedWordsHistory,
      successWordsHistory,
      missedWordsHistory,
    } = useStudy();
    const { currentSpeechSentance } = useQuiz();
    const [audioUrl, setAudioUrl] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [index, setIndex] = React.useState(0);
    const [audioUrls, setAudioUrls] = React.useState({});
    const { classes } = useStyles();

    const student = JSON.parse(localStorage.getItem("profile")) || {};
    // const contentArray = ['The garden was full of beautiful, colorful flowers that swayed gently in the breeze.', 'The garden was full of beautiful, colorful flowers that swayed gently in the breeze.', 'The garden was full of beautiful, colorful flowers that swayed gently in the breeze.', 'The garden was full of beautiful, colorful flowers that swayed gently in the breeze.', 'The garden was full of beautiful, colorful flowers that swayed gently in the breeze.', 'The garden was full of beautiful, colorful flowers that swayed gently in the breeze.', 'The garden was full of beautiful, colorful flowers that swayed gently in the breeze.', 'The garden was full of beautiful, colorful flowers that swayed gently in the breeze.', 'The garden was full of beautiful, colorful flowers that swayed gently in the breeze.']
    const total = React.useRef(contentArray.length);
    const audioRef = React.useRef(null);
    const played = React.useRef(false);
    const isLoadingAsync = React.useRef(false);
    const playbackStarted = React.useRef(false);
    const apiFailure = React.useRef(false);
    const loadedFromCache = React.useRef(false);

    const togglePlayPause = () => {
      if (!audioUrl) return;
      playbackStarted.current = true;

      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
        if (
          !played.current &&
          onAudioPlayback &&
          typeof onAudioPlayback === "function"
        ) {
          onAudioPlayback();
        }

        played.current = true;
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleEnd = () => {
      setIsPlaying(false);

      if (index + 1 != total.current) {
        setIndex((prev) => prev + 1);
      } else {
        if (
          onAudioPlaybackComplete &&
          typeof onAudioPlaybackComplete === "function"
        ) {
          onAudioPlaybackComplete();
        }
      }
    };

    const handleRetry = () => {
      togglePlayPause();
      setIsPlaying(false);
      setIndex(0);

      if (onAudioReplay && typeof onAudioReplay === "function") {
        onAudioReplay();
      }
    };

    const loadAudioUrl = async (text) => {
      if (!text) return;
      if (apiFailure.current || readingComplete) return;

      try {
        let conf = await getSpeechConfig(
          String(student.geographicalType).toLowerCase(),
          student.learningGeneration,
        );
        console.log("from asnctexttospeech");
        const data = await textToSpeech({ text: text, ...conf });

        if (data && data.response && data.response.audio) {
          return data.response.audio;
        }
      } catch (error) {
        let message = error.message;

        if (error.response) {
          let { data } = error.response;
          let { info } = data;

          message = info.detail;
        }
        apiFailure.current = true;
        toast.error(message);
      }
    };

    const loadAudioUrls = async (obj) => {
      isLoadingAsync.current = true;

      if (apiFailure.current || loadedFromCache.current || readingComplete) {
        setIsLoading(false);
        return;
      }

      for (const key in obj) {
        if (Object.hasOwnProperty.call(obj, key)) {
          const element = obj[key];
          // Skip items that already have audio loaded
          if (element.audio) continue;
          let audio = await loadAudioUrl(element.text);

          setAudioUrls((prev) => ({ ...prev, [key]: { ...element, audio } }));
        }
      }

      setIsLoading(false);
      isLoadingAsync.current = false;
    };

    React.useEffect(() => {
      let cache = JSON.parse(localStorage.getItem("audio_urls_" + id)) || {};

      if (Object.keys(cache).length) {
        loadedFromCache.current = true;
        setAudioUrls(cache);
      }
    }, []);

    React.useEffect(() => {
      if (autoPlay && enableTextToAudio) {
        setIsLoading(true);

        if (apiFailure.current || loadedFromCache.current || readingComplete) {
          setIsLoading(false);
          return;
        }

        loadAudioUrl(contentArray[index])
          .then((audio) => setAudioUrl(audio))
          .catch((err) => {})
          .finally(() => setIsLoading(false));
      }
    }, [autoPlay, enableTextToAudio]);

    React.useEffect(() => {
      if (loadedFromCache.current) return;

      setAudioUrls((prev) => {
        // Only reset entries whose text actually changed
        let changed = false;
        const next = { ...prev };
        contentArray.forEach((str, ind) => {
          const trimmed = str.trim();
          if (!next[ind] || next[ind].text !== trimmed) {
            next[ind] = { text: trimmed, audio: null, played: false };
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }, [contentArray]);

    React.useEffect(() => {
      if (!Object.keys(audioUrls).length) return;

      let loaded = Object.values(audioUrls).filter((e) => e.audio);

      if (loaded.length == total.current && loadedFromCache.current) {
        setAudioUrl(audioUrls[index].audio);
        setIsLoading(false);
        return;
      }

      if (
        index < loaded.length &&
        loaded.length <= total.current &&
        playbackStarted.current &&
        !isPlaying
      ) {
        if (
          audioUrls[index]["played"] &&
          !audioUrls[loaded.length - 1]["played"]
        ) {
          setIndex(loaded.length - 1);
        } else if (!audioUrls[index]["played"]) {
          setAudioUrl(audioUrls[index].audio);
        }
      }

      if (loaded.length == total.current) {
        if (id) {
          localStorage.setItem("audio_urls_" + id, JSON.stringify(audioUrls));
        }

        return;
      }

      if (
        !isLoadingAsync.current &&
        !apiFailure.current &&
        !loadedFromCache.current &&
        !readingComplete &&
        enableTextToAudio
      ) {
        loadAudioUrls(audioUrls);
      }
    }, [audioUrls]);

    React.useEffect(() => {
      if (!Object.keys(audioUrls).length) return;

      if (index <= total.current - 1) {
        setAudioUrl(audioUrls[index].audio);
      }
    }, [index]);

    React.useEffect(() => {
      if (audioUrl && index !== 0) {
        togglePlayPause();
        handlePlay();
        setAudioUrls((prev) => ({
          ...prev,
          [index]: { ...prev[index], played: true },
        }));
      }
    }, [audioUrl]);

    return (
      <React.Fragment>
        <Box sx={{ width: "fit-content" }}>
          {contentArray && contentArray.length > 0 && (
            <div>
              {contentArray.map((e, i) => {
                let splitted = e.split(" ");
                let isActive = syncWithSpeechEvaluation
                  ? i == currentSpeechSentance
                  : i == index;

                // const scoreItem = scores.find((score) => score.index === i);
                const scoreItem = scores.find(
                  (score) => parseInt(score.index) === i,
                );

                return (
                  <Box
                    key={`content-${i}`}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <>
                      {Object.keys(scores).length > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            minWidth: "70px",
                          }}
                        >
                          <img
                            style={{ width: "30%" }}
                            src={
                              scoreItem
                                ? scoreItem?.score > 0
                                  ? correctSvg
                                  : wrongSvg
                                : ""
                            }
                            alt=""
                          />
                        </Box>
                      )}
                    </>

                    <Typography
                      sx={{
                        fontSize: {
                          xs: "0.8em",
                          sm: "0.8em",
                          md: "1em",
                          lg: "1em",
                          xl: "1.2em",
                        },
                        fontFamily: '"Quicksand", sans-serif',
                        fontWeight: "700",
                        background:
                          !speechEvaluated && isActive ? "#FFC74A33" : "",
                        border:
                          !speechEvaluated && isActive
                            ? "1px solid #FFC74A"
                            : "",
                        padding: 1,
                        // margin: 1,
                        borderRadius: 1,
                      }}
                      key={i}
                    >
                      {splitted.map((itm, idx) => {
                        let html =
                          String(itm).trim() + (splitted.length > 1 ? " " : "");
                        let synonym = dictionary?.find(
                          (s) => s.text.toLowerCase() == itm.toLowerCase(),
                        );
                        let title = "<None>";

                        // Get failed/success/missed words for this sentence from history
                        const sentenceFailedWords = failedWordsHistory[i] || [];
                        const sentenceSuccessWords =
                          successWordsHistory[i] || [];
                        const sentenceMissedWords =
                          missedWordsHistory[i] || [];

                        // Check if this word is failed, success, or missed
                        const lowerWord = itm
                          .replace(/[^a-zA-Z]/g, "")
                          .toLowerCase();
                        const failedWordsSet = new Set(
                          sentenceFailedWords?.map(({ word, index }) =>
                            JSON.stringify({
                              word: word
                                ?.replace(/[^a-zA-Z]/g, "")
                                .toLowerCase(),
                              index,
                            }),
                          ),
                        );
                        const successWordsSet = new Set(
                          sentenceSuccessWords?.map(({ word, index }) =>
                            JSON.stringify({
                              word: word
                                ?.replace(/[^a-zA-Z]/g, "")
                                .toLowerCase(),
                              index,
                            }),
                          ),
                        );
                        const missedWordsSet = new Set(
                          sentenceMissedWords?.map(({ word, index }) =>
                            JSON.stringify({
                              word: word
                                ?.replace(/[^a-zA-Z]/g, "")
                                .toLowerCase(),
                              index,
                            }),
                          ),
                        );
                        const wordKey = JSON.stringify({
                          word: lowerWord,
                          index: idx,
                        });
                        const isMissed = missedWordsSet.has(wordKey);
                        const isFailed =
                          !isMissed && failedWordsSet.has(wordKey);
                        const isSuccess =
                          !isMissed && successWordsSet.has(wordKey);

                        if (synonym && Object.keys(synonym).length) {
                          if (synonym.definition && synonym.synonyms?.length) {
                            title = (
                              <Box>
                                <Typography
                                  sx={{
                                    color: "#000",
                                    fontSize: "0.8em",
                                    fontFamily: '"Quicksand", sans-serif',
                                    fontWeight: "700",
                                  }}
                                >
                                  <span style={{ color: "#9E6AFF" }}>
                                    {html}
                                  </span>
                                  : {synonym.definition}
                                </Typography>
                                <Typography
                                  sx={{
                                    color: "#000",
                                    fontSize: "0.8em",
                                    fontFamily: '"Quicksand", sans-serif',
                                    fontWeight: "600",
                                  }}
                                >
                                  <span style={{ fontWeight: 700 }}>
                                    Synonyms
                                  </span>
                                  : {synonym.synonyms.join(", ")}
                                </Typography>
                              </Box>
                            );
                          } else if (
                            synonym.synonyms?.length &&
                            !synonym.definition
                          ) {
                            title = (
                              <Box>
                                <Typography
                                  sx={{
                                    color: "#000",
                                    fontSize: "0.8em",
                                    fontFamily: '"Quicksand", sans-serif',
                                    fontWeight: "700",
                                  }}
                                >
                                  <span style={{ fontWeight: 700 }}>
                                    Synonyms
                                  </span>
                                  : {synonym.synonyms.join(", ")}
                                </Typography>
                              </Box>
                            );
                          } else if (
                            !synonym.synonyms?.length &&
                            synonym.definition
                          ) {
                            title = (
                              <Box>
                                <Typography
                                  sx={{
                                    color: "#000",
                                    fontSize: "0.8em",
                                    fontFamily: '"Quicksand", sans-serif',
                                    fontWeight: "700",
                                  }}
                                >
                                  <span style={{ color: "#9E6AFF" }}>
                                    {html}
                                  </span>
                                  : {synonym.definition}
                                </Typography>
                              </Box>
                            );
                          }
                        }

                        // Highlight missed (omitted) words in black
                        if (isMissed) {
                          return (
                            <span
                              key={idx}
                              style={{ fontWeight: "700", color: "#000000" }}
                            >
                              {itm}{" "}
                            </span>
                          );
                        }

                        // Highlight failed words in red
                        if (isFailed) {
                          return (
                            <span
                              key={idx}
                              style={{ fontWeight: "700", color: "red" }}
                            >
                              {itm}{" "}
                            </span>
                          );
                        }

                        // Highlight success words in green
                        if (isSuccess) {
                          return (
                            <span
                              key={idx}
                              style={{ fontWeight: "700", color: "#42AF3C" }}
                            >
                              {itm}{" "}
                            </span>
                          );
                        }

                        if (importantWords?.includes(itm)) {
                          return (
                            <LightTooltip
                              arrow
                              placement="bottom-start"
                              title={title}
                              sx={{ fontSize: "1.2rem" }}
                            >
                              <span
                                key={idx}
                                style={styles.importantWord}
                                dangerouslySetInnerHTML={{ __html: html }}
                              />
                            </LightTooltip>
                          );
                        }
                        return (
                          <span
                            key={idx}
                            dangerouslySetInnerHTML={{ __html: html }}
                          />
                        );
                      })}
                      {splitted.length > 1 ? (
                        <br />
                      ) : contentArray.length - 1 == i ? (
                        ""
                      ) : (
                        ", "
                      )}
                    </Typography>
                  </Box>
                );
              })}
            </div>
          )}

          {helpText && Object.keys(helpText).length > 0 && (
            <HelpText style={styles.helpText} data={helpText} />
          )}

          {enableTextToAudio && (
            <div style={styles.hearButtonContainer}>
              <Button
                endIcon={
                  !isLoading && !audioUrl ? (
                    <VolumeUpIcon />
                  ) : isPlaying ? (
                    <PauseCircleIcon />
                  ) : (
                    <PlayCircleIcon />
                  )
                }
                size={"small"}
                type={"secondary"}
                style={styles.button}
                placeholder={
                  isLoading
                    ? "Loading..."
                    : audioUrl
                      ? isPlaying
                        ? "Pause"
                        : "Play"
                      : "Loading..."
                }
                onClick={audioUrl ? togglePlayPause : null}
                disabled={isLoading || !audioUrl}
              />
            </div>
          )}
        </Box>
        {enableTextToAudio && (
          <audio
            ref={audioRef}
            src={audioUrl}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={handleEnd}
          />
        )}

        {readingComplete && (
          <div style={styles.replayBtnContainer}>
            <Button
              size={"small"}
              type={"secondary"}
              placeholder={
                <React.Fragment>
                  Replay
                  <ReplayIcon />
                </React.Fragment>
              }
              onClick={handleRetry}
            />
          </div>
        )}
      </React.Fragment>
    );
  };

  const TextComponent =
    contentDisplayType == "FIB"
      ? FIBText.bind(null, {
          disabled,
          value: selected,
          renderDefault,
          handleAnswerSelection,
          length: props.length,
          content,
        })
      : Component;

  return renderDefault ? (
    <TextComponent />
  ) : (
    <DisplayTemplate
      templateType={templateType}
      components={[TextComponent]}
      {...props}
    />
  );
}

const styles = {
  container: {
    textAlign: "left",
    // display: 'flex'
  },
  button: {
    marginLeft: "10px",
    background: "transparent",
    fontWeight: "bold",
    position: "absolute",
    top: "10px",
    right: "20px",
  },
  helpText: {},
  text: {
    marginTop: "auto",
    marginBottom: "auto",
    lineHeight: "25px",
    maxWidth: "80%",
  },
  activeText: {
    fontWeight: "bold",
    backgroundColor: "yellow",
  },
  hearButtonContainer: {
    display: "flex",
    alignItems: "start",
  },
  replayBtnContainer: {
    marginTop: "1.2rem",
    display: "flex",
    justifyContent: "center",
  },
  importantWord: {
    color: "rgba(158, 106, 255, 1)",
    cursor: "pointer",
  },
};
